import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatMs } from '@/features/admin/processing-format'
import type { ProcessingPipelineState } from '@/services/api/adminProcessing'
import { ActiveJobsMobileList } from './ActiveJobsMobileList'
import { CountChip } from './CountChip'

export function ActiveJobsCard({
  pipeline,
  rangeLabel,
}: {
  pipeline: ProcessingPipelineState
  rangeLabel: string
}) {
  const { t } = useTranslation()
  const m = pipeline.metrics
  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
        <div>
          <CardTitle className="text-base">
            {t('admin.processing.activeJobs.title')} ({pipeline.active.length})
          </CardTitle>
          <CardDescription>
            {t('admin.processing.activeJobs.description', { range: rangeLabel })}
          </CardDescription>
        </div>
        {m && (
          <div className="flex flex-wrap gap-2">
            <CountChip
              label={t('admin.processing.activeJobs.done')}
              value={m.jobs.done}
              tone="ok"
            />
            <CountChip
              label={t('admin.processing.activeJobs.failed')}
              value={m.jobs.failed}
              tone="bad"
            />
            <CountChip
              label={t('admin.processing.activeJobs.running')}
              value={m.jobs.running}
              tone="info"
            />
            <CountChip
              label={t('admin.processing.activeJobs.pending')}
              value={m.jobs.pending}
              tone="muted"
            />
          </div>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-2">
        {pipeline.active.length === 0 ? (
          <div className="px-2 py-2 text-sm text-muted-foreground">
            {t('admin.processing.activeJobs.empty')}
          </div>
        ) : (
          <Table className="max-md:hidden">
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.processing.cols.job')}</TableHead>
                <TableHead>{t('admin.processing.activeJobs.colPipeline')}</TableHead>
                <TableHead>{t('admin.processing.activeJobs.colDocument')}</TableHead>
                <TableHead>{t('admin.processing.cols.step')}</TableHead>
                <TableHead className="text-right">
                  {t('admin.processing.activeJobs.colElapsed')}
                </TableHead>
                <TableHead className="text-right">
                  {t('admin.processing.activeJobs.colEta')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pipeline.active.map(j => (
                <TableRow key={j.job_id}>
                  <TableCell className="font-mono text-xs">{j.job_id.slice(0, 8)}</TableCell>
                  <TableCell>{j.pipeline}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {j.document_id?.slice(0, 8) ?? '—'}
                  </TableCell>
                  <TableCell>
                    {j.current_step ? (
                      <Badge variant="secondary" className="gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {j.current_step}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMs(j.elapsed_ms)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatMs(j.eta_ms)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <ActiveJobsMobileList jobs={pipeline.active} />
      </CardContent>
    </Card>
  )
}
